import React, { useState, useEffect, useCallback, useRef } from 'react'
import { Card, Box, Stack, Group, Avatar, Text, ActionIcon } from '@mantine/core'
import {
  IoHeart,
  IoHeartOutline,
  IoChatbubbleOutline,
  IoShareSocialOutline,
  IoOpenOutline,
  IoTrashOutline
} from 'react-icons/io5'
import { useHistory } from 'react-router-dom'
import agent from '../../app/api/agent'
import { useStore } from '../../app/stores/store'
import { toAbsoluteMediaUrl } from '../../app/api/resolveApiBaseUrl'
import ShootsCanvasRenderer from '../ShootsCanvasRenderer'
import { CommentsDrawer } from './CommentsDrawer'
import { FEED_CARD_HOVER, FEED_ACTION_BAR_SX } from './feedCardTheme'

export interface ShootDto {
  id: string;
  userId: string;
  mediaUrl: string;
  mediaType: 'image' | 'video';
  textBoxesJson?: string;
  createdAt: string;
  expiresAt?: string;
  userAvatar: string;
  userName: string;
  likeCount?: number;
  commentCount?: number;
  /** Optional "Make Model · cartridge" line from API. */
  linkedCaption?: string;
}

interface ShootCardProps {
  shoot: ShootDto;
  /** If omitted, `POST /api/shoots/{id}/like` is used. */
  onLike?: (shootId: string, liked: boolean) => Promise<void>;
  onCommentClick?: (shootId: string) => void;
  onShare?: (shootId: string) => void;
  /** After successful delete (feed removes row). */
  onDeleted?: (shootId: string) => void;
  /** Hide open / delete chrome (e.g. full-page view). */
  hideToolbar?: boolean;
}

function formatExpiresIn (expiresAt: string | undefined): string {
  if (!expiresAt) return ''
  const diffMs = new Date(expiresAt).getTime() - Date.now()
  if (diffMs <= 0) return 'Expired'
  const hours = Math.floor(diffMs / (1000 * 60 * 60))
  const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60))
  if (hours >= 1) return `Expires in ${hours} hour${hours === 1 ? '' : 's'}`
  if (minutes < 1) return 'Expires soon'
  return `Expires in ${minutes} minute${minutes === 1 ? '' : 's'}`
}

function getRelativeTime (dateString: string): string {
  const diffMs = Date.now() - new Date(dateString).getTime()
  const minutes = Math.floor(diffMs / (1000 * 60))
  const hours = Math.floor(diffMs / (1000 * 60 * 60))
  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24))
  if (minutes < 60) return `${minutes}m ago`
  if (hours < 24) return `${hours}h ago`
  return `${days}d ago`
}

export const ShootCard: React.FC<ShootCardProps> = ({
  shoot,
  onLike,
  onCommentClick,
  onShare,
  onDeleted,
  hideToolbar
}) => {
  const history = useHistory()
  const { userStore } = useStore()
  const [isLiked, setIsLiked] = useState(false)
  const [isLoadingLike, setIsLoadingLike] = useState(false)
  const [likeCount, setLikeCount] = useState(shoot.likeCount ?? 0)
  const [commentsOpen, setCommentsOpen] = useState(false)
  const mediaContainerRef = useRef<HTMLDivElement | null>(null)
  const [mediaSize, setMediaSize] = useState({ width: 0, height: 0 })

  const mediaSrc = toAbsoluteMediaUrl(shoot.mediaUrl)

  useEffect(() => {
    setLikeCount(shoot.likeCount ?? 0)
  }, [shoot.id, shoot.likeCount])

  useEffect(() => {
    const node = mediaContainerRef.current
    if (!node) return

    const updateSize = () => {
      const width = node.clientWidth
      const height = node.clientHeight
      setMediaSize((prev) => (prev.width === width && prev.height === height ? prev : { width, height }))
    }

    updateSize()
    const observer = new ResizeObserver(updateSize)
    observer.observe(node)
    return () => observer.disconnect()
  }, [])

  const handleLike = useCallback(async () => {
    if (isLoadingLike) return
    setIsLoadingLike(true)
    const prevLiked = isLiked
    const prevCount = likeCount
    try {
      if (onLike) {
        const next = !isLiked
        setIsLiked(next)
        await onLike(shoot.id, next)
      } else {
        const res = await agent.Shoots.toggleLike(shoot.id)
        setIsLiked(res.isLiked)
        setLikeCount(res.newLikeCount)
      }
    } catch (error) {
      setIsLiked(prevLiked)
      setLikeCount(prevCount)
      console.error('Failed to like shoot:', error)
    } finally {
      setIsLoadingLike(false)
    }
  }, [isLiked, isLoadingLike, likeCount, onLike, shoot.id])

  const openComments = () => {
    if (onCommentClick) {
      onCommentClick(shoot.id)
      return
    }
    setCommentsOpen(true)
  }

  const canManage =
    userStore.user?.id === shoot.userId || userStore.isAdmin || userStore.isModerator

  const handleDelete = useCallback(async () => {
    if (!canManage) return
    if (!window.confirm('Delete this shoot? This cannot be undone.')) return
    try {
      await agent.Shoots.deleteShoot(shoot.id)
      onDeleted?.(shoot.id)
    } catch (e) {
      console.error(e)
    }
  }, [canManage, onDeleted, shoot.id])

  return (
    <>
      <Card
        padding={0}
        radius="md"
        withBorder
        sx={{
          width: '100%',
          overflow: 'hidden',
          borderColor: 'var(--gs-border)',
          backgroundColor: 'var(--gs-bg-2)',
          ...FEED_CARD_HOVER
        }}
      >
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            backgroundColor: '#000'
          }}
        >
          <Box
            ref={mediaContainerRef}
            sx={{
              position: 'relative',
              width: '100%',
              aspectRatio: '9 / 16',
              maxHeight: 'min(85vh, 720px)',
              margin: '0 auto',
              backgroundColor: '#000'
            }}
          >
            {shoot.mediaType === 'video'
              ? (
              <video
                src={mediaSrc}
                autoPlay
                muted
                loop
                playsInline
                controls={false}
                disablePictureInPicture
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  display: 'block'
                }}
              />
                )
              : (
              <img
                src={mediaSrc}
                alt=""
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  display: 'block'
                }}
              />
                )}
            {mediaSize.width > 0 && mediaSize.height > 0
              ? (
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  zIndex: 4,
                  pointerEvents: 'none'
                }}
              >
                <ShootsCanvasRenderer
                  mediaUrl={mediaSrc}
                  mediaType={shoot.mediaType === 'image' ? 'photo' : 'video'}
                  textBoxesJson={shoot.textBoxesJson ?? ''}
                  width={mediaSize.width}
                  height={mediaSize.height}
                  renderMedia={false}
                />
              </Box>
                )
              : null}
            {!hideToolbar && (
              <Group
                spacing={4}
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  zIndex: 5
                }}
              >
                <ActionIcon
                  variant="filled"
                  color="dark"
                  radius="xl"
                  size="md"
                  onClick={() => history.push(`/slop-feed/shoot/${shoot.id}`)}
                  aria-label="Open shoot"
                >
                  <IoOpenOutline size={18} />
                </ActionIcon>
                {canManage && (
                  <ActionIcon
                    variant="filled"
                    color="red"
                    radius="xl"
                    size="md"
                    onClick={() => void handleDelete()}
                    aria-label="Delete shoot"
                  >
                    <IoTrashOutline size={18} />
                  </ActionIcon>
                )}
              </Group>
            )}
            <Box
              sx={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                padding: '12px',
                background: 'linear-gradient(to top, rgba(0,0,0,0.85), transparent)',
                zIndex: 3
              }}
            >
              {shoot.linkedCaption
                ? (
                <Text size="xs" c="rgba(255,255,255,0.9)" mb={6} lineClamp={2}>
                  {shoot.linkedCaption}
                </Text>
                  )
                : null}
              <Group spacing="sm" mb={6} noWrap>
                <Avatar src={shoot.userAvatar || undefined} alt={shoot.userName} size={32} radius="xl" />
                <Stack spacing={2} style={{ flex: 1, minWidth: 0 }}>
                  <Text size="sm" fw={600} c="white" lineClamp={1}>
                    {shoot.userName}
                  </Text>
                  <Text size="xs" c="rgba(255,255,255,0.75)">
                    {getRelativeTime(shoot.createdAt)}
                  </Text>
                </Stack>
              </Group>
              {shoot.expiresAt
                ? (
                <Text size="xs" fw={600} c="var(--gs-accent)" tt="uppercase" style={{ letterSpacing: 0.5 }}>
                  {formatExpiresIn(shoot.expiresAt)}
                </Text>
                  )
                : null}
            </Box>
          </Box>
        </Box>

        <Group spacing="md" sx={FEED_ACTION_BAR_SX} align="center" wrap="nowrap">
          <ActionIcon
            variant="subtle"
            size="lg"
            color={isLiked ? 'red' : 'gray'}
            onClick={() => void handleLike()}
            disabled={isLoadingLike}
            aria-label={isLiked ? 'Unlike' : 'Like'}
          >
            {isLiked ? <IoHeart size={22} /> : <IoHeartOutline size={22} />}
          </ActionIcon>
          <ActionIcon variant="subtle" size="lg" color="gray" onClick={openComments} aria-label="Comments">
            <IoChatbubbleOutline size={22} />
          </ActionIcon>
          <ActionIcon
            variant="subtle"
            size="lg"
            color="gray"
            onClick={() => onShare?.(shoot.id)}
            aria-label="Share"
            sx={{ marginLeft: 'auto' }}
          >
            <IoShareSocialOutline size={22} />
          </ActionIcon>
          <Text size="xs" c="dimmed" style={{ flexShrink: 0 }}>
            {likeCount} {likeCount === 1 ? 'like' : 'likes'}
            {(shoot.commentCount ?? 0) > 0 ? ` · ${shoot.commentCount} comments` : ''}
          </Text>
        </Group>
      </Card>

      <CommentsDrawer
        opened={commentsOpen}
        onClose={() => setCommentsOpen(false)}
        contentId={shoot.id}
        contentLabel="Shoot / Slop"
      />
    </>
  )
}

export default ShootCard
